import {
  checkHasHideFlag,
  QUERY_STRING_KEYS,
  QUERY_STRING_VALUES,
  useIsFirstTo,
  useMyCategoryCache,
  WelcomeModal,
} from '@inflearn/modals';
import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { API_ENDPOINT, useV2MyCategoryQuery, useV2SessionQuery } from '../../@types/uccSchema';
import { CDN_INTEGRATION_URL } from '../../appConfig';
import { eventNames } from '../../constants/gtmEventNames';
import { EVENT_ID } from '../../constants/MODAL_EVENT_ID';
import { MY_CATEGORY_BLACK_LIST_PATHS } from '../../constants/MY_CATEGORY_BLACK_LIST_PATHS';
import { useDomExist } from '../../hooks/useDomExist';
import { useForAntManModalDisclosure } from '../../hooks/useForAntManModalDisclosure';
import { useTrackEvent } from '../../utils/useTrackEvent';

export default function WelcomeModalWrapper() {
  const location = useLocation();
  const isBlackListPath = MY_CATEGORY_BLACK_LIST_PATHS.some((path) =>
    matchPath(path, location.pathname)
  );

  const { data: session } = useV2SessionQuery({
    queryKey: [API_ENDPOINT.V2_SESSION],
    url: API_ENDPOINT.V2_SESSION,
  });
  const { isCached } = useMyCategoryCache(session?.userId);
  const hasNoShowMyCategoryFlag = checkHasHideFlag(session?.userId || 0);

  if (!session?.userId || isBlackListPath || isCached || hasNoShowMyCategoryFlag) {
    return null;
  }

  return <_WelcomeModal />;
}

function _WelcomeModal() {
  const { trackEvent } = useTrackEvent();
  const [isWelcomeModalOpened, welcomeModalHandlers] = useForAntManModalDisclosure({
    eventId: EVENT_ID.WELCOME_MODAL,
    initialState: false,
  });

  const isGroupNoticeExist = useDomExist(document.querySelector('.gr-notice-modal-cover'));
  const isFirstHello = useIsFirstTo({
    enabled: isWelcomeModalOpened,
    valueKey: QUERY_STRING_VALUES.FIRST_HELLO,
    onSuccessPipe: (searchParams) => {
      searchParams.set(QUERY_STRING_KEYS.TO, QUERY_STRING_VALUES.IS_FIRSTREGISTER);

      return searchParams;
    },
  });

  const { data: session } = useV2SessionQuery({
    queryKey: [API_ENDPOINT.V2_SESSION],
    url: API_ENDPOINT.V2_SESSION,
  });
  const { data: myCategories } = useV2MyCategoryQuery({
    queryKey: [API_ENDPOINT.V2_MY_CATEGORY],
    url: API_ENDPOINT.V2_MY_CATEGORY,
    options: {
      enabled: !!session?.userId,
    },
  });

  const { setCache } = useMyCategoryCache(session?.userId);

  const handleClose = () => {
    const eventName = isFirstHello
      ? eventNames.CLICK_WELCOME_BUTTON
      : eventNames.CLICK_MYCATEGORY_GUIDE_BUTTON;

    void trackEvent(eventName);
    welcomeModalHandlers.close();
    window.dispatchEvent(new Event(EVENT_ID.MY_CATEGORY_MODAL));
  };

  const hasMyCategory = !!myCategories && myCategories.length !== 0;
  useEffect(() => {
    if (document.querySelector('.gr-notice-modal-cover')) {
      return;
    }

    if (!myCategories) {
      return;
    }

    if (hasMyCategory) {
      setCache();

      return;
    }

    welcomeModalHandlers.open();
  }, [myCategories, hasMyCategory]);

  useEffect(() => {
    if (isFirstHello) {
      void trackEvent(eventNames.COMPLETE_SIGNUP);
      void trackEvent(eventNames.COMPLETE_REGISTRATION);
    }
  }, [isFirstHello]);

  const isOpened = isWelcomeModalOpened && !!session && !isGroupNoticeExist;
  const animationUrl = isFirstHello
    ? `${CDN_INTEGRATION_URL}/image/apng/hugging-face.png`
    : `${CDN_INTEGRATION_URL}/image/apng/heart-with-arrow.png`;

  return (
    <WelcomeModal
      lang="ko"
      userName={session?.userName || ''}
      animationUrl={animationUrl}
      isOpened={isOpened}
      onClose={handleClose}
      withConfetti={isFirstHello}
    />
  );
}
